import { Navigate, Route, Routes } from 'react-router-dom';
import { lazy, Suspense, useContext } from 'react';
import { UserContext } from '@context/createContext';

import Login from '@modules/realestateLogin/login/Login.jsx';
import Layout from '@layout/Layout.jsx';
import Loader from '@modules/loading/Loader';
import DubzAssistant from '@pages/DubzAssistant.jsx';
import TestNumber from '@pages/TestNumber.jsx';

const Dashboard = lazy(() => import('@pages/Dashboard.jsx'));
const Agents = lazy(() => import('@pages/Agents.jsx'));
const AdsListWrapper = lazy(() => import('@/pages/AdsListWrapper.jsx'));
const AdsDetails = lazy(() => import('@pages/AdsDetails.jsx'));
const AgentsDetails = lazy(() => import('@pages/AgentsDetails.jsx'));
const AdsCreate = lazy(() => import('@pages/AdsCreate.jsx'));
const AdsEdit = lazy(() => import('@pages/AdsEdit.jsx'));
const Subscription = lazy(() => import('@pages/Subscription.jsx'));
const Settings = lazy(() => import('@pages/Settings.jsx'));
const Requests = lazy(() => import('@pages/Requests.jsx'));
const VisitRequests = lazy(() => import('@pages/VisitRequests.jsx'));
const Customers = lazy(() => import('@pages/Customers.jsx'));
const FailureBilling = lazy(() => import('@pages/FailureBilling'));
const SuccessBilling = lazy(() => import('@pages/SuccessBilling'));
const SubscriptionAgent = lazy(() => import('@pages/SubscriptionAgent'));
const AgentsHandler = lazy(() => import('@pages/AgentsHandler'));
const Reminders = lazy(() => import('@pages/Reminders'));
const TermsAndConditions = lazy(() => import('@pages/TermsAndConditions'));
const MarketStatus = lazy(() => import('@modules/dashboard/MarketStatus.jsx'));
const QRCodeGenerator = lazy(() => import('@pages/QRCodeGenerator.jsx'));
const AgentQRCodeGenerator = lazy(() => import('@pages/AgentQRCodeGenerator.jsx'));
// const HelpCenter = lazy(() => import("@pages/HelpCenter"));
// const HelpCenterSection = lazy(() => import("@pages/HelpCenterSection"));

import 'react-loading-skeleton/dist/skeleton.css';
import Providers from './layout/providers';

// import "react-toastify/dist/ReactToastify.css";

function App() {
  const { user, isLoading, realEstate } = useContext(UserContext);
  return (
    <Providers>
      <Routes>
        <Route path="/dubz-assistant" element={<DubzAssistant />} />
        <Route path="/testnumber" element={<TestNumber />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        {isLoading ? (
          <Route
            path="*"
            element={
              <div className="flex items-center justify-center h-screen w-screen">
                <Loader />
              </div>
            }
          />
        ) : !user ? (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        ) : (
          <Route path="/" element={<Layout />}>
            {getRoutesBasedOnUserAndSubscription(user, realEstate)}
          </Route>
        )}
      </Routes>
    </Providers>
  );
}

function getRoutesBasedOnUserAndSubscription(user, realEstate) {
  if (user?.RealEstateId) {
    if (realEstate?.subscriptions.Active) {
      return user.UserType === 'RealEstate'
        ? getOwnerRoutesWithActiveSubscription()
        : user.UserType === 'Agent' && getAgentRoutesWithActiveSubscription();
    } else {
      return user.UserType === 'RealEstate'
        ? getOwnerRoutesWithExpiredSubscription()
        : user.UserType === 'Agent'
          ? getAgentRoutesWithExpiredSubscription()
          : null;
    }
  } else {
    return user.UserType === 'RealEstate'
      ? getOwnerRoutesWithExpiredSubscription()
      : user.UserType === 'Agent' && getAgentRoutesWithoutRealstate();
  }
}

// کاربر owner با realstate, subscription
function getOwnerRoutesWithActiveSubscription() {
  return (
    <>
      <Route path="*" element={<Navigate to="/" />} />
      <Route path="/" element={<Dashboard />} />
      <Route path="/reminders" element={<Reminders />} />
      <Route path="ads/list" element={<AdsListWrapper />} />
      <Route path="ads/list/:id" element={<AdsDetails />} />
      <Route path="ads/create" element={<AdsCreate />} />
      <Route path="ads/edit/:id" element={<AdsEdit />} />
      <Route path="customers" element={<Customers />} />
      <Route path="agents/invations" element={<Requests />} />
      <Route path="visit-requests" element={<VisitRequests />} />
      <Route path="settings" element={<Settings />} />
      <Route path="agents/list" element={<Agents />} />
      <Route path="agents/list/:id" element={<AgentsDetails />} />
      <Route path="subscription" element={<Subscription />} />
      <Route path="billing/invoiceorder/failure" element={<FailureBilling />} />
      <Route path="billing/invoiceorder/success" element={<SuccessBilling />} />
      <Route path="market-status" element={<MarketStatus />} />
      <Route path="qr-code" element={<QRCodeGenerator />} />
      <Route path="agent-qr-code" element={<AgentQRCodeGenerator />} />
    </>
  );
}

// کاربر agent با realstate, subscription
function getAgentRoutesWithActiveSubscription() {
  return (
    <>
      <Route path="*" element={<Navigate to="/" />} />
      <Route path="/" element={<Dashboard />} />
      <Route path="/reminders" element={<Reminders />} />
      <Route path="ads/list" element={<AdsListWrapper />} />
      <Route path="ads/list/:id" element={<AdsDetails />} />
      <Route path="ads/create" element={<AdsCreate />} />
      <Route path="ads/edit/:id" element={<AdsEdit />} />
      <Route path="customers" element={<Customers />} />
      <Route path="requests" element={<Requests />} />
      <Route path="visit-requests" element={<VisitRequests />} />
      <Route path="settings" element={<Settings />} />
      <Route path="market-status" element={<MarketStatus />} />
      <Route path="agent-qr-code" element={<AgentQRCodeGenerator />} />
    </>
  );
}

// کاربر owner با محدودیت در اشتراک
function getOwnerRoutesWithExceededAgentsLimit() {
  return (
    <>
      <Route path="/" element={<AgentsHandler />} />
      <Route path="*" element={<Navigate to="/" />} />
    </>
  );
}

// کاربر agent بدون realstate
function getAgentRoutesWithoutRealstate() {
  return (
    <>
      <Route path="requests" element={<Requests />} />
      <Route path="visit-requests" element={<VisitRequests />} />
      <Route path="/" element={<Navigate to="/requests" />} />
      <Route path="*" element={<Navigate to="/requests" />} />
    </>
  );
}

// کاربر owner بدون subscription
function getOwnerRoutesWithExpiredSubscription() {
  return (
    <>
      <Route path="subscription" element={<Subscription />} />
      <Route path="billing/invoiceorder/failure" element={<FailureBilling />} />
      <Route path="billing/invoiceorder/success" element={<SuccessBilling />} />
      <Route path="/" element={<Navigate to="/subscription" />} />
      <Route path="*" element={<Navigate to="/subscription" />} />
    </>
  );
}

// کاربر agent بدون subscription
function getAgentRoutesWithExpiredSubscription() {
  return (
    <>
      <Route path="subscription" element={<SubscriptionAgent />} />
      <Route path="/" element={<Navigate to="/subscription" />} />
      <Route path="*" element={<Navigate to="/subscription" />} />
    </>
  );
}

export default App;
